<template>
  <base-switcher
    class="tw-mb-8 tw-overflow-x-auto"
    :class="[{ [Classes.USER_NAVBAR]: userTabs }, Classes.NAVBAR]"
    :model-value="modelValue"
    :options="navigationTabs"
    @input="updateTab"
  >
    <div class="tw-flex tw-items-center">
      <base-button
        v-if="allowFiltering && browse && !userTabs"
        class="tw-px-4 mod-input--height tw-group hover:game-active-button-color-hover"
        basic-btn
        @click="$emit('toggle:filters')"
      >
        <font-awesome-icon
          icon="sliders-h"
          class="tw-hidden sm:tw-inline tw-mr-1 tw-transition-colors group-hover:game-active-text-hover"
        />
        <font-awesome-icon
          icon="sliders-h"
          size="lg"
          class="sm:tw-hidden tw-mr-1 tw-transition-colors group-hover:game-active-text-hover"
        />
        <h6
          class="tw-hidden sm:tw-inline tw-transition-all tw-opacity-75 group-hover:game-active-text-hover group-hover:tw-opacity-100"
        >
          {{ filterMenuOpen ? $t('hide_filters') : $t('show_filters') }}
        </h6>
      </base-button>
      <a
        v-if="
          modelValue === BROWSE_TABS.MODS &&
          browse &&
          !view &&
          !userTabs &&
          $route.name !== MOD_ADD_ROUTE &&
          allowAddingMods
        "
        href="#/m/add"
        class="tw-ml-2"
        @click="onAddMod"
      >
        <base-button primary>{{
          $t('add_mod', { ugc: ugcNameTextSingular })
        }}</base-button>
      </a>
      <base-link
        v-if="modelValue === BROWSE_TABS.MODS && edit"
        :to="{ name: MOD_VIEW_ROUTE }"
        class="tw-ml-2"
      >
        <base-button primary>{{
          $t('view_mod', { ugc: ugcNameTextSingular })
        }}</base-button>
      </base-link>
      <base-link
        v-if="modelValue === BROWSE_TABS.MODS && mod && view && isModManager"
        :to="
          allowEditingMods
            ? { name: MOD_ADMIN_SETTINGS_ROUTE }
            : `${mod.profile_url}/admin`
        "
        :target="allowEditingMods ? '_self' : '_blank'"
        class="tw-ml-2"
        :class="Classes.EDIT_BUTTON"
      >
        <tooltip-hover
          fixed
          position="bottom"
          :text="$t(allowEditingMods ? 'admin' : 'edit_mod')"
        >
          <base-button primary icon="pen" input-btn />
        </tooltip-hover>
      </base-link>
      <profile-dropdown v-if="!userTabs" />
    </div>
  </base-switcher>
</template>

<script>
import ProfileDropdown from '@components/Users/ProfileDropdown.vue'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { BROWSE_TABS, ITEM_TYPES } from '@config/options.js'
import { filterMenuOpen } from '@composables/useFilter.js'
import { setGlobalModal } from '@composables/useModal.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import { SS_REDIRECT } from '@config/localstorage.js'
import { watchEffect, computed, toRefs } from 'vue'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { profileFrom } from '@router'
import { useI18n } from 'vue-i18n'
import {
  USER_COLLECTION_BROWSE_ROUTE,
  MOD_BROWSE_ROUTE_TOKEN_PACKS,
  MOD_ADMIN_SETTINGS_ROUTE,
  COLLECTION_BROWSE_ROUTE,
  USER_GUIDE_BROWSE_ROUTE,
  USER_MOD_BROWSE_ROUTE,
  GAME_FEATURED_ROUTE,
  GUIDE_BROWSE_ROUTE,
  MOD_BROWSE_ROUTE,
  MOD_VIEW_ROUTE,
  LIBRARY_ROUTE,
  MOD_ADD_ROUTE,
  CONTENT_ROUTE,
} from '@config/routeNames.js'
import {
  collectionFollowingStore,
  partnerProgramStore,
  subscriptionStore,
  localModStore,
  authStore,
  gameStore,
  modStore,
} from '@stores'
import {
  isMonetizationPartnerProgramPreviewEnabled,
  isTokenMarketplaceEnabled,
} from '@helpers/bitHelper.js'
import {
  useResourceValue,
  usePermissions,
  useNavSubMenu,
  useRoute,
} from '@composables'

export default {
  components: {
    ProfileDropdown,
    TooltipHover,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    view: {
      type: Boolean,
      default: false,
    },
    browse: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    userTabs: {
      type: Boolean,
      default: false,
    },
    backButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'toggle:filters'],
  setup(props, { emit }) {
    const { modelValue, userTabs, backButton } = toRefs(props)

    const { hasPermissionForMod, hasPermissionForGame } = usePermissions()
    const { followedCollections } = collectionFollowingStore()
    const { isInPartnerProgram } = partnerProgramStore()
    const { subscriptions } = subscriptionStore()
    const { updateTab: updateLibraryTab } = useNavSubMenu('library')
    const { ugcName } = useResourceValue()
    const { localMods } = localModStore()
    const { push, replace, getModId } = useRoute()
    const { isLoggedIn } = authStore()
    const { getModByNameId } = modStore()
    const { getPublicGame } = gameStore()
    const { t } = useI18n()

    const game = getPublicGame()
    const modNameId = getModId().value
    const mod = getModByNameId(game.value.name_id, modNameId)

    const allowEditingMods = computed(() => Settings.value.allowEditingMods)
    const allowAddingMods = computed(() => Settings.value.allowAddingMods)
    const allowFiltering = computed(() => Settings.value.allowFiltering)

    const ugcNameText = computed(() => {
      let _ugcNameText = ugcName(null, false, false).value

      if (['mods', 'levels'].includes(_ugcNameText.toLowerCase())) {
        _ugcNameText = t(_ugcNameText.split('s')[0].toLowerCase(), 2)
      }

      return _ugcNameText
    })

    let ugcNameTextSingular = ugcName(null, true, true).value

    if (ugcNameTextSingular === 'mods') ugcNameTextSingular = t('mod')
    else if (ugcNameTextSingular === 'levels') ugcNameTextSingular = t('level')

    const navigationTabs = computed(() => {
      if (backButton.value) {
        return [
          {
            value: 'back',
            icon: 'chevron-left',
            text: t('back'),
          },
        ]
      }
      const tabs = []

      if (!userTabs.value && Settings.value.showFeatured) {
        tabs.push({
          value: BROWSE_TABS.FEATURED,
          text: t('featured'),
          to: {
            name: GAME_FEATURED_ROUTE,
          },
        })
      }

      tabs.push({
        value: BROWSE_TABS.MODS,
        text: ugcNameText.value,
        to: {
          name: userTabs.value ? USER_MOD_BROWSE_ROUTE : MOD_BROWSE_ROUTE,
        },
      })

      const partnerProgram =
        !isMonetizationPartnerProgramPreviewEnabled(
          game.value.monetization_options
        ) ||
        isInPartnerProgram(game.value.id).value ||
        hasPermissionForGame()

      if (Settings.value.showCollections) {
        tabs.push({
          value: BROWSE_TABS.COLLECTIONS,
          text: t('collection', 2),
          to: {
            name: userTabs.value
              ? USER_COLLECTION_BROWSE_ROUTE
              : COLLECTION_BROWSE_ROUTE,
          },
        })
      }

      if (Settings.value.showGuides) {
        tabs.push({
          value: BROWSE_TABS.GUIDES,
          text: t('guide', 2),
          to: {
            name: userTabs.value ? USER_GUIDE_BROWSE_ROUTE : GUIDE_BROWSE_ROUTE,
          },
        })
      }

      if (
        !userTabs.value &&
        Settings.value.showMarketplace &&
        isTokenMarketplaceEnabled(game.value.monetization_options) &&
        partnerProgram
      ) {
        tabs.push({
          value: BROWSE_TABS.TOKEN_PACKS,
          text: t('marketplace.currency_packs'),
          to: {
            name: MOD_BROWSE_ROUTE_TOKEN_PACKS,
          },
        })
      }

      if (allowEditingMods.value && !userTabs.value) {
        tabs.push({
          value: BROWSE_TABS.CONTENT,
          text: t('my_content'),
          to: { name: CONTENT_ROUTE },
        })
      }

      if (Settings.value.showLibrary && !userTabs.value) {
        const libraryCount =
          subscriptions.value.length +
          followedCollections.value.length +
          (localMods.value?.length || 0)
        tabs.push({
          value: BROWSE_TABS.LIBRARY,
          text: t('library'),
          count: libraryCount,
          to: { name: LIBRARY_ROUTE },
        })
      }
      return tabs
    })

    const isModManager = computed(() =>
      hasPermissionForMod(modNameId, game.value.name_id, 'manager')
    )

    function triggerModal(path) {
      sessionStorage.setItem(SS_REDIRECT, '/' + path)
      setGlobalModal({ modal: MODAL_LOGIN })
    }

    function updateTab(e, input) {
      updateLibraryTab(ITEM_TYPES.MOD)

      if (input === 'back') {
        goBack()
      } else if (
        [BROWSE_TABS.LIBRARY, BROWSE_TABS.CONTENT].includes(input) &&
        !isLoggedIn.value
      ) {
        e.preventDefault()
        triggerModal(input)
      } else {
        emit('update', input)
      }
    }

    function onAddMod(e) {
      if (!isLoggedIn.value) {
        e.preventDefault()
        sessionStorage.setItem(SS_REDIRECT, '/m/add')
        setGlobalModal({ modal: MODAL_LOGIN })
      }
    }

    function goBack() {
      if (profileFrom.value) {
        replace(profileFrom.value)
        profileFrom.value = undefined
      } else {
        push({ path: '/' })
      }
    }

    watchEffect(() => {
      if (!navigationTabs.value.some((t) => t.value === modelValue.value)) {
        replace(navigationTabs.value[0].to)
      }
    })

    return {
      MOD_ADMIN_SETTINGS_ROUTE,
      ugcNameTextSingular,
      allowEditingMods,
      allowAddingMods,
      allowFiltering,
      filterMenuOpen,
      navigationTabs,
      MOD_VIEW_ROUTE,
      MOD_ADD_ROUTE,
      isModManager,
      BROWSE_TABS,
      updateTab,
      onAddMod,
      Classes,
      mod,
    }
  },
}
</script>
